import type { FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
    ArrowRight,
    LoaderCircle,
    Mail,
    MailCheck,
    Send
} from 'lucide-react'
import { validateEmail } from '@/lib/validation'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/generals/button'
import AuthShell from '@/components/auth/auth-shell'
import { Field, FormAlert } from '@/components/auth/form-field'

const ForgotPasswordPage: FC = (): ReactNode => {
    const [email, setEmail] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [formError, setFormError] = useState<string | null>(null)
    const [sentMessage, setSentMessage] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const emailError = validateEmail(email)
        setError(emailError)
        setFormError(null)

        if (emailError) return

        setSubmitting(true)

        try {
            const response = await fetch('/api/auth/forgot', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            })

            const data = (await response.json().catch(() => null)) as { message?: string } | null

            if (!response.ok) {
                setFormError(data?.message ?? "درخواست ثبت نشد. دوباره امتحان کنید.")
                return
            }

            setSentMessage(data?.message ?? "اگر حسابی با این ایمیل وجود داشته باشد، لینک تعیین رمز تازه برایش فرستاده شد.")
        }
        catch {
            setFormError("ارتباط برقرار نشد. اتصال اینترنت را بررسی و دوباره امتحان کنید.")
        }
        finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            <Seo title="بازیابی رمز عبور" description="لینک تعیین رمز عبور تازه را به ایمیل خود دریافت کنید." noIndex />

            <AuthShell
                title="رمز عبور را فراموش کرده‌اید؟"
                description="ایمیل حساب خود را وارد کنید تا لینک تعیین رمز تازه برایتان فرستاده شود."
                footer={
                    <Link href='/login' className='inline-flex items-center gap-1.5 font-bold text-primary hover:underline'>
                        <ArrowRight className='size-4' aria-hidden='true' />
                        بازگشت به صفحه ورود
                    </Link>
                }
            >
                <AnimatePresence mode='wait' initial={false}>
                    {sentMessage ? (
                        <motion.div
                            key='sent'
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex flex-col items-center gap-4 rounded-3xl bg-white p-8 text-center shadow-soft ring-1 ring-border'
                            role='status'
                        >
                            <span className='grid size-14 place-items-center rounded-2xl bg-success/10 text-success'>
                                <MailCheck className='size-7' aria-hidden='true' />
                            </span>
                            <p className='text-base font-bold text-foreground'>ایمیل را بررسی کنید</p>
                            <p className='text-sm text-muted-foreground'>{sentMessage}</p>
                            <button
                                type='button'
                                onClick={() => setSentMessage(null)}
                                className='text-xs font-semibold text-primary hover:underline'
                            >
                                ایمیل دیگری وارد می‌کنم
                            </button>
                        </motion.div>
                    ) : (
                        <motion.form
                            key='form'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onSubmit={handleSubmit}
                            noValidate
                            className='flex flex-col gap-5'
                        >
                            {formError && <FormAlert>{formError}</FormAlert>}

                            <Field
                                id='forgot-email'
                                label="ایمیل حساب"
                                type='email'
                                icon={Mail}
                                ltr
                                autoComplete='email'
                                inputMode='email'
                                placeholder="you@example.com"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                error={error}
                                disabled={submitting}
                            />

                            <Button type='submit' size='lg' className='w-full' disabled={submitting}>
                                {submitting ? <LoaderCircle className='animate-spin' aria-hidden='true' /> : <Send className='rotate-180' aria-hidden='true' />}
                                {submitting ? "در حال ارسال..." : "ارسال لینک بازیابی"}
                            </Button>
                        </motion.form>
                    )}
                </AnimatePresence>
            </AuthShell>
        </>
    )
}

export default ForgotPasswordPage
